import { useEffect, useMemo, useState } from "react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";

export default function Options() {
  const [strategies, setStrategies] = useState([]);
  const [symbol, setSymbol] = useState("SPY");
  const [strategy, setStrategy] = useState("iron_condor");
  const [days, setDays] = useState(30);
  const [iv, setIv] = useState(30);
  const [width, setWidth] = useState(5);
  const [contracts, setContracts] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/options/strategies").then(({ data }) => setStrategies(data.strategies || []));
  }, []);

  const build = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/options/strategy", {
        symbol: symbol.toUpperCase(),
        strategy,
        days_to_expiry: Number(days),
        iv_pct: Number(iv),
        risk_free_pct: 4.5,
        width_pct: Number(width),
        contracts: Number(contracts),
      });
      setResult(data);
      toast.success(`${data.strategy.replace(/_/g, " ").toUpperCase()} built · ${symbol.toUpperCase()} @ ${data.spot}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Strategy build failed");
    } finally {
      setLoading(false);
    }
  };

  const activeStrat = strategies.find((s) => s.id === strategy);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="options-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">
          ◆ OPTIONS STRATEGY BUILDER · BLACK-SCHOLES GREEKS
        </div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">
          Options Lab
        </h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Design iron condors, straddles, spreads, and calendars. See the payoff curve, break-evens,
          max profit / loss, and portfolio Greeks in real time.
        </p>
      </div>

      {/* Strategy picker */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-0 border border-[color:var(--border-default)] mb-4" data-testid="strategy-picker">
        {strategies.map((s) => (
          <button
            key={s.id}
            data-testid={`strategy-${s.id}`}
            onClick={() => setStrategy(s.id)}
            className={`p-3 border-r border-b border-[color:var(--border-default)] text-left transition-colors ${
              strategy === s.id ? "bg-[color:var(--bg-hover)]" : "hover:bg-[color:var(--bg-elevated)]"
            }`}
          >
            <div className={`text-[10px] font-mono-data tracking-widest ${strategy === s.id ? "text-gold" : "text-[color:var(--text-secondary)]"}`}>
              {s.outlook}
            </div>
            <div className={`text-sm font-body mt-1 ${strategy === s.id ? "text-white" : "text-[color:var(--text-secondary)]"}`}>
              {s.name}
            </div>
          </button>
        ))}
      </div>

      {activeStrat && (
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-4 text-xs font-body text-[color:var(--text-secondary)] mb-4 leading-relaxed" data-testid="strategy-description">
          <span className="text-gold font-mono-data text-[10px] tracking-widest mr-2">◆ {activeStrat.name.toUpperCase()}</span>
          {activeStrat.description}
        </div>
      )}

      {/* Inputs */}
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="options-inputs">
        <Input label="SYMBOL" value={symbol} onChange={setSymbol} testid="opt-symbol" />
        <Input label="DTE" value={days} onChange={setDays} testid="opt-dte" type="number" />
        <Input label="IV %" value={iv} onChange={setIv} testid="opt-iv" type="number" />
        <Input label="WIDTH %" value={width} onChange={setWidth} testid="opt-width" type="number" />
        <Input label="CONTRACTS" value={contracts} onChange={setContracts} testid="opt-contracts" type="number" />
        <button
          data-testid="build-strategy-btn"
          onClick={build}
          disabled={loading}
          className="bg-white text-black text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40 transition-colors"
        >
          {loading ? "BUILDING…" : "BUILD →"}
        </button>
      </div>

      {result && <StrategyResult r={result} />}
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value.toUpperCase())}
        className="bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1.5 text-sm text-white font-mono-data focus:outline-none focus:border-[color:var(--border-focus)]"
      />
    </label>
  );
}

function StrategyResult({ r }) {
  const chartData = useMemo(() => r.payoff_curve || [], [r]);
  return (
    <div className="space-y-4" data-testid="strategy-result">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="strategy-summary">
        <Cell label="SPOT" value={fmt(r.spot)} />
        <Cell label="MAX PROFIT" value={`$${fmt(r.max_profit)}`} tone={r.max_profit >= 0 ? "bull" : "bear"} />
        <Cell label="MAX LOSS" value={`$${fmt(r.max_loss)}`} tone={r.max_loss >= 0 ? "bull" : "bear"} />
        <Cell label="NET DEBIT / (CREDIT)" value={`$${fmt(r.net_debit)}`} tone={r.net_debit >= 0 ? "" : "bull"} />
        <Cell label="BREAK-EVEN(S)" value={r.breakevens.length ? r.breakevens.map((b) => fmt(b)).join(" · ") : "—"} />
      </div>

      {/* Payoff chart */}
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4" data-testid="payoff-chart">
        <div className="text-[10px] font-mono-data text-gold tracking-widest mb-2">◆ PAYOFF AT EXPIRY</div>
        <div style={{ height: 320, minWidth: 300, minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="price" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#27272A" }} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#121212", border: "1px solid #27272A", fontSize: 11, fontFamily: "JetBrains Mono" }}
                labelStyle={{ color: "#A1A1AA" }}
                itemStyle={{ color: "#fff" }}
                formatter={(v) => [`$${fmt(v)}`, "P&L"]}
              />
              <ReferenceLine y={0} stroke="#52525B" strokeDasharray="3 3" />
              <ReferenceLine x={r.spot} stroke="#FFD700" strokeDasharray="4 4" label={{ value: `Spot ${r.spot}`, fill: "#FFD700", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideTopRight" }} />
              {r.breakevens.map((be) => (
                <ReferenceLine key={be} x={be} stroke="#A1A1AA" strokeDasharray="2 2" />
              ))}
              <Line
                type="monotone"
                dataKey="pnl"
                stroke="#32D74B"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legs + Greeks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[color:var(--border-default)]">
        <div className="p-4 border-r border-[color:var(--border-default)]" data-testid="legs-panel">
          <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">LEGS</div>
          <div className="space-y-2">
            {r.legs.map((leg, i) => (
              <div key={`leg-${i}-${leg.type}-${leg.strike}`} className="grid grid-cols-[auto,auto,1fr,auto] gap-2 items-center border-b border-[color:var(--border-default)] py-2 last:border-0">
                <span className={`text-[10px] font-mono-data tracking-widest ${leg.action === "buy" ? "text-bull" : "text-bear"}`}>
                  {leg.action.toUpperCase()}
                </span>
                <span className="text-[10px] font-mono-data text-[color:var(--text-secondary)] tracking-widest">{leg.type.toUpperCase()}</span>
                <span className="font-mono-data text-sm text-white">@ {fmt(leg.strike)}</span>
                <span className="font-mono-data text-xs text-gold">${fmt(leg.price)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4" data-testid="greeks-panel">
          <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">PORTFOLIO GREEKS</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(r.portfolio_greeks).map(([k, v]) => (
              <div key={k} className="border border-[color:var(--border-default)] p-2">
                <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{k.toUpperCase()}</div>
                <div className={`font-mono-data text-sm ${v >= 0 ? "text-bull" : "text-bear"}`}>
                  {v >= 0 ? "+" : ""}{fmt(v, 4)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-white";
  return (
    <div className="p-3 border-r border-b border-[color:var(--border-default)]">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-lg mt-1 ${c}`}>{value}</div>
    </div>
  );
}
